import { LitElement, html, css, nothing, PropertyValues, unsafeCSS } from "lit";
import { property, customElement, query, queryAll, queryAssignedElements, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { when } from "lit/directives/when.js";
import { repeat } from "lit/directives/repeat.js";
import { classMap } from "lit/directives/class-map.js";
import { resolveColor } from "../helper/utils/color-resolver";
import { cssOnFillEnhance } from "../helper/utils/contrast-color";
import { PopoverController } from "../helper/controllers/popover-controller";
import {
  FormControlBase,
  FormState,
} from "../helper/internals/form-control-base";
import { SkyIcon } from "../sky-icon/sky-icon.js";
import { SkyCheckbox } from "../sky-checkbox/sky-checkbox.js";
import { SkyInput } from "../sky-input/sky-input.js";
import { PresetController } from "../helper/controllers/preset-controller";
import {
  arrayPropHasChanged,
  coerceArray,
  EMPTY_ARRAY,
} from "../helper/utils/stable-array";

export type SkyComboboxJustify =
  | "flex-start"
  | "center"
  | "flex-end"
  | "space-between"
  | "space-around"
  | "space-evenly";
export type SkyComboboxErrorDisplayMode = "single" | "all" | "none";
export type SkyComboboxIconPosition = "left" | "right";
/** How multi-select values render in the field (single-select always uses plain text). */
export type SkyComboboxMultiDisplay = "chips" | "text" | "count";
export type SkyComboboxValueChangedDetail = {
  value: any | any[];
  selectedItems: any[];
  emittedValues?: any | any[];
  isFilteredSelection?: boolean;
};
export type SkyComboboxSearchQueryChangedDetail = { searchQuery: string };
export type SkyComboboxValidationErrorDetail = { errors: string[] };
export type SkyComboboxValidationSuccessDetail = { value: any };
export type SkyComboboxIconClickDetail = {
  position: SkyComboboxIconPosition;
  event: MouseEvent;
};
export type SkyComboboxAddNewOptionDetail = { value: string };

/** Reserved key on `customRenderers` for renderer UI state (not an option renderer). */
export const COMBOBOX_RENDERERS_STATE_KEY = "$rendererState";

export type SkyComboboxRendererStateApi = {
  rendererState: Readonly<Record<string, unknown>>;
  patchRendererState: (patch: Record<string, unknown>) => void;
};

export type SkyComboboxOptionRenderContext = SkyComboboxRendererStateApi & {
  /** Index within the currently filtered dropdown list. */
  index: number;
  /** Index in the original `options` array. */
  sourceIndex: number;
  /** Plain display label (no highlight markup). */
  label: string;
  /** Search-highlighted label HTML string (same as default option text). */
  highlightedLabel: string;
  selected: boolean;
  /** Keyboard / pointer active row. */
  active: boolean;
  multiSelect: boolean;
};

export type SkyComboboxSelectedRenderContext = SkyComboboxRendererStateApi & {
  selectedItems: any[];
  /** Index within `selectedItems`. */
  index: number;
  multiSelect: boolean;
  multiDisplay: SkyComboboxMultiDisplay;
  /** Default plain label for this option. */
  label: string;
};

export type SkyComboboxOptionRenderer = (
  option: unknown,
  ctx: SkyComboboxOptionRenderContext,
) => unknown;

export type SkyComboboxSelectedRenderer = (
  option: unknown,
  ctx: SkyComboboxSelectedRenderContext,
) => unknown;

export type SkyComboboxCustomRenderers = {
  /** Custom dropdown option row content (host keeps checkbox / selected chrome). */
  option?: SkyComboboxOptionRenderer;
  /** Custom selected-value / chip label content. */
  selected?: SkyComboboxSelectedRenderer;
  [COMBOBOX_RENDERERS_STATE_KEY]?: Record<string, unknown>;
};

/** Shared empty sentinel so null/undefined rebinds do not look like a new value. */
const EMPTY_RENDERERS: SkyComboboxCustomRenderers = Object.freeze({});

function samePrimitiveArray(a: unknown[], b: unknown[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function isComboboxRendererStateBag(
  value: unknown,
): value is Record<string, unknown> {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const o = value as Record<string, unknown>;
  return typeof o.option !== "function" && typeof o.selected !== "function";
}

function extractComboboxRendererState(
  customRenderers: SkyComboboxCustomRenderers | null | undefined,
): Record<string, unknown> {
  const bag = customRenderers?.[COMBOBOX_RENDERERS_STATE_KEY];
  return isComboboxRendererStateBag(bag) ? { ...bag } : {};
}

/**
 * @element sky-combobox
 *
 * @summary Interactive combobox with single/multi select, search, chips, validation, and popover-based option selection.
 *
 * @status stable
 * @since 1.0.0
 *
 * @documentation https://sky-ui.com/components/combobox
 * @dependency sky-icon
 * @dependency sky-checkbox
 * @dependency sky-input
 *
 * @uiVModel value value-changed
 * @uiVModel searchQuery search-query-changed
 *
 * @slot icon-left - Slot for the left icon in the combobox (e.g., search icon).
 * @slot icon-right - Slot for the right icon in the combobox (e.g., dropdown icon).
 * @slot - Default slot for adding custom options or content.
 *
 * @csspart combo-box-container - The main container element wrapping the entire component
 * @csspart input-wrapper - The input field wrapper that contains chips, icons, and dropdown button
 * @csspart icon-slot - Both left and right icon containers
 * @csspart placeholder-label - The placeholder text label
 * @csspart clear-button - The clear (×) button
 * @csspart field-trailing - Trailing actions rail (clear, right icon, chevron)
 * @csspart dropdown-button - The dropdown toggle arrow button
 * @csspart chip-container - Container for the chips in multi-select mode
 * @csspart value-summary - Multi-select text/count summary (when `multi-display` is `text` or `count`)
 * @csspart chip - Individual chip element
 * @csspart single-select-chip - Chip shown in single-select mode
 * @csspart overflow-chip - The "+N" chip that shows hidden chips count
 * @csspart remove-chip-icon - The remove icon within chips
 * @csspart hidden-chips-dropdown - Dropdown that appears when clicking overflow chip
 * @csspart hidden-chip-inner - Individual chip in the hidden chips dropdown
 * @csspart hidden-chip-label - Label text in hidden chips dropdown
 * @csspart dropdown - The main dropdown container
 * @csspart search-container - Container for the search input in the dropdown
 * @csspart search-input - The search input field
 * @csspart add-option-icon - The "+" icon for adding new options
 * @csspart select-all - The "Select All" row in the dropdown
 * @csspart checkbox - Checkbox elements in multi-select mode
 * @csspart option - Individual option row in the dropdown
 * @csspart highlighted-text - Highlighted text in search results
 * @csspart no-options - "No options found" message
 * @csspart error-list - Container for validation error messages
 * @csspart error-item - Individual validation error message
 * @csspart dropdown-scroller - Scrollable container for dropdown options
 * @csspart chip-label - Label text within chips
 *
 * @fires {CustomEvent<SkyComboboxValueChangedDetail>} value-changed - Fired whenever selection changes.
 * @fires {CustomEvent<SkyComboboxSearchQueryChangedDetail>} search-query-changed - Fired when search query changes.
 * @fires {CustomEvent<SkyComboboxValidationErrorDetail>} validation-error - Fired when validation fails.
 * @fires {CustomEvent<SkyComboboxValidationSuccessDetail>} validation-success - Fired when validation passes.
 * @fires {CustomEvent<SkyComboboxIconClickDetail>} icon-click - Fired when icon slot is clicked.
 * @fires {CustomEvent<SkyComboboxAddNewOptionDetail>} add-new-option - Fired when user confirms adding a new option.
 *
 * @property {any} value - Current selection value (single) or value array (multi).
 * @property {string} placeholder - Main placeholder text.
 * @property {string} searchPlaceholder - Search input placeholder text.
 * @property {string} searchQuery - Current search filter text when `searchable` is enabled. Default: `""`.
 * @property {any[]} options - Available options list.
 * @property {boolean} searchable - Enables search input and filtering.
 * @property {boolean} addOption - Enables ad-hoc option creation from search query.
 * @property {boolean} multiSelect - Enables multi-select chip behavior.
 * @property {boolean} selectAll - Shows select-all row in eligible modes.
 * @property {boolean} clearable - Shows clear button when value exists.
 * @property {boolean} loading - Shows loading affordance.
 * @property {boolean} compact - Enables compact sizing.
 * @property {boolean} disabled - Disables interaction.
 * @property {boolean} required - Enables required validation.
 * @property {boolean} readonly - Enables read-only mode.
 * @property {boolean} emitFullObject - Emits full option objects in events.
 * @property {string} emittedKey - Key emitted from object options when `emitFullObject` is false.
 * @property {string} displayKey - Key used for visual labels in object options.
 * @property {SkyComboboxCustomRenderers} customRenderers - Named renderers: `option` (dropdown row), `selected` (value/chip label), optional `$rendererState`.
 * @property {number} visibleChips - Number of visible chips before `+N` overflow chip.
 * @property {number} chipSize - Maximum label length for each chip.
 * @property {string} color - Accent color token/CSS color.
 * @property {number} zIndex - Dropdown overlay z-index.
 * @property {string} height - Max dropdown scroll height.
 * @property {boolean} openAbove - Opens dropdown above when true.
 * @property {SkyComboboxJustify} justify - Option-row content justification.
 * @property {boolean} showErrors - Renders validation errors below control.
 * @property {SkyComboboxErrorDisplayMode} errorDisplayMode - Validation error rendering strategy.
 * @property {(value:any)=>true|string} validations - External validator callbacks.
 * @property {boolean} validationArrayFormat - Sends selected array into validators when true.
 * @property {string} prefix - Optional inline prefix text.
 * @property {string} suffix - Optional inline suffix text.
 * @property {string} prefixColor - Prefix color value.
 * @property {string} suffixColor - Suffix color value.
 * @property {string} requiredMessage - Required validation failure text.
 * @property {boolean} enforceMinOneSelection - Prevents clearing all selections in multi-select mode.
 * @property {boolean} validationActive - Enables validation UI state.
 * @property {boolean} invalid - Reflects invalid state.
 * @property {string} variant - Visual style: `default`, `highlight`, `fieldset`, or `inside`.
 * @property {string} label - Label text. Above the field by default; on the border for `fieldset`; inside the field for `inside`.
 * @property {SkyComboboxMultiDisplay} multiDisplay - Multi-select value UI: `chips` (default), `text` (comma-joined), or `count` (“N selected”).
 * @property {string} preset - Named prop preset from nearest `sky-config-provider`. Default: `""`.
 *
 * @method openDropdown Opens the combobox dropdown.
 * @method closeDropdown Closes the combobox dropdown.
 * @method clearSelection Clears current selection if allowed.
 * @method reset Resets combobox state and clears validation UI.
 * @method patchRendererState Merges keys into `customRenderers.$rendererState` and re-renders.
 *
 * @example
 * ```html
 * <sky-combobox label="Assignee" searchable clearable placeholder="Select user"></sky-combobox>
 * ```
 * ```vue
 * <template>
 *   <sky-combobox label="Assignee" searchable clearable placeholder="Select user"></sky-combobox>
 * </template>
 * ```
 * ```jsx
 * export default function Demo() {
 *   return <sky-combobox label="Assignee" searchable clearable placeholder="Select user"></sky-combobox>;
 * }
 * ```
 */
@customElement("sky-combobox")
export class SkyCombobox extends FormControlBase {

  static dependencies: Record<string, CustomElementConstructor> = {
    "sky-icon": SkyIcon,
    "sky-checkbox": SkyCheckbox,
    "sky-input": SkyInput,
  };

  /** Current value. Set via attribute in HTML (e.g. value="option-1") or via JS (string or array when multiSelect). Not reflected when set to array from JS. */
  @property() value: any = "";
  @property({ type: String }) placeholder: string = "Select An Option";
  @property({ type: String }) searchPlaceholder: string = "Search...";
  @property({ type: String }) label: string = "";
  private _options: any[] = EMPTY_ARRAY;
  @property({ type: Array, hasChanged: arrayPropHasChanged })
  get options(): any[] {
    return this._options;
  }
  set options(value: any[] | null | undefined) {
    this._options = coerceArray(value);
  }
  @property({ type: Number }) chipSize: number = 20;
  @property({ type: String }) color: string = "primary";
  @property({ type: Boolean, reflect: true }) clearable: boolean = false;
  @property({ type: Boolean, reflect: true }) loading: boolean = false;
  @property({ type: Boolean, reflect: true }) compact: boolean = false;
  @property({ type: Boolean, reflect: true }) override disabled: boolean = false;
  @property({ type: Boolean, reflect: true }) searchable: boolean = false;
  @property({ type: Boolean, reflect: true }) multiSelect: boolean = false;
  @property({ type: Number, reflect: true }) visibleChips: number = 2;
  @property({ type: Boolean, reflect: true }) selectAll: boolean = false;
  private _validations: ((value: any) => true | string)[] = EMPTY_ARRAY;
  @property({ type: Array, hasChanged: arrayPropHasChanged })
  get validations(): ((value: any) => true | string)[] {
    return this._validations;
  }
  set validations(value: ((value: any) => true | string)[] | null | undefined) {
    this._validations = coerceArray(value);
  }
  @property({ type: Boolean }) showErrors: boolean = false;
  @property({ type: String }) errorDisplayMode: SkyComboboxErrorDisplayMode = "single";
  @property({ type: Boolean, reflect: true }) override required: boolean = false;
  @property({ type: Boolean, reflect: true }) emitFullObject: boolean = false;
  @property({ type: String }) emittedKey: string = "value";
  @property({ type: String }) displayKey: string = "";
  private _customRenderers: SkyComboboxCustomRenderers = EMPTY_RENDERERS;
  /**
   * Named custom renderers for option rows and selected-value labels.
   * Coerces null/undefined to `{}` for safe framework binding.
   */
  @property({ type: Object })
  get customRenderers(): SkyComboboxCustomRenderers {
    return this._customRenderers;
  }
  set customRenderers(value: SkyComboboxCustomRenderers | null | undefined) {
    this._customRenderers =
      value != null && typeof value === "object" && !Array.isArray(value)
        ? value
        : EMPTY_RENDERERS;
  }
  @property({ type: Boolean }) addOption: boolean = false;
  @property({ type: Boolean }) validationArrayFormat: boolean = false;
  @property({ type: String }) override prefix: string = "";
  @property({ type: String }) suffix: string = "";
  @property({ type: String }) prefixColor: string = "";
  @property({ type: String }) suffixColor: string = "";
  @property({ type: String }) justify: SkyComboboxJustify = "flex-start";
  @property({ type: Number }) zIndex: number = 11;
  @property({ type: String }) height: string = "200px";
  @property({ type: String }) override requiredMessage: string = "Selection is required.";
  @property({ type: Boolean, reflect: true }) override readonly: boolean = false;
  @property({ type: Boolean }) enforceMinOneSelection: boolean = false;
  @property({ type: Boolean }) openAbove: boolean = false;
  @property({ type: Boolean, reflect: true }) validationActive: boolean = false;
  @property({ type: Boolean, reflect: true }) invalid = false;
  @property({ type: String, reflect: true }) variant:
    | "default"
    | "highlight"
    | "fieldset"
    | "inside" = "default";
  /** Multi-select field presentation. Ignored for single-select. */
  @property({ type: String, reflect: true, attribute: "multi-display" })
  multiDisplay: SkyComboboxMultiDisplay = "chips";

  /** Current search filter when `searchable`; exposed for controlled search / `v-model:search-query`. */
  @property({ type: String, attribute: false }) searchQuery = "";
  @state() filteredOptions: any[] = [];
  @state() focused: boolean = false;
  @state() open: boolean = false;
  @state() selectedItems: any[] = [];
  @state() hiddenChipsOpen: boolean = false;
  @state() activeIndex: number = -1;
  @state() validationErrors: string[] = [];
  @state() validationStatus: any[] = [];
  @state() private hasLeftIcon = false;
  @state() private hasRightIcon = false;
  @state() private optionSlotSet = new Set<string>();
  private _rendererRuntimeState: Record<string, unknown> = {};

  @query(".input-wrapper") private inputWrapperEl!: HTMLElement;
  @query(".dropdown") private dropdownEl!: HTMLElement;
  @query(".hidden-chips-dropdown") private hiddenDropdownEl!: HTMLElement;
  @query(".search-container sky-input") private searchInputEl!: SkyInput;
  @query(".overflow-chip") private overflowChipEl!: HTMLElement;
  @query(".combo-box-container") private comboContainerEl!: HTMLElement;
  @queryAll("[data-row-index]") private rowEls!: NodeListOf<HTMLElement>;
  @queryAssignedElements({ slot: "icon-left" }) private iconLeftEls!: HTMLElement[];
  @queryAssignedElements({ slot: "icon-right" }) private iconRightEls!: HTMLElement[];

  private _searchDebounceId: ReturnType<typeof setTimeout> | null = null;
  /** When true, skip willUpdate filtering so typing debounce can delay list work. */
  private _deferSearchFilter = false;
  private _closeResetId: ReturnType<typeof setTimeout> | null = null;
  private static readonly _SEARCH_DEBOUNCE_MS = 150;
  private static readonly _SEARCH_DEBOUNCE_OPTIONS_THRESHOLD = 30;
  /** Matches PopoverController exit animation so list stays visible while closing. */
  private static readonly _CLOSE_RESET_MS = 250;

  private dropdownPopover = new PopoverController(this, {
    getAnchor: () => this.inputWrapperEl,
    getPopover: () => this.dropdownEl,
    cssVarLeft: "--sky-dd-left",
    cssVarTop: "--sky-dd-top",
    cssVarWidth: "--sky-dd-width",
    getPlacement: () => 'bottom' as any,
    getAlign: () => "start" as any,
    getGap: () => 6,
    getPadding: () => 8,
    getMatchAnchorWidth: () => true,
    getMaxWidthToViewport: () => true,
    getMaxWidthMode: () => "cap" as any,
    getFlip: () => true,
    getFlipOrder: () => [],
    getAnimateReposition: () => true,
    getRepositionMs: () => 160,
    getCloseOnEscape: () => true,
    getCloseOnOutside: () => true,
    getRestoreFocusOnClose: () => true,
    onOpenChange: (open, meta) => {
      this.open = open;

      if (open) {
        this.focused = true;
        if (this._closeResetId != null) {
          clearTimeout(this._closeResetId);
          this._closeResetId = null;
        }
        this.validationActive = true;
        this.filterOptions();
        requestAnimationFrame(() => this.focusOnOpen());
      } else {
        this.activeIndex = -1;
        // Defer search reset so options stay populated during the close animation.
        if (this._closeResetId != null) clearTimeout(this._closeResetId);
        this._closeResetId = setTimeout(() => {
          this._closeResetId = null;
          if (this.open) return;
          this.searchQuery = "";
          this.emitSearchQueryChanged();
          this.filterOptions();
        }, SkyCombobox._CLOSE_RESET_MS);

        // close hidden chips too
        this.hiddenChipsOpen = false;
        this.hiddenPopover.closePopover("api");
      }
    },
  });

  private hiddenPopover = new PopoverController(this, {
    getAnchor: () => this.overflowChipEl,
    getPopover: () => this.hiddenDropdownEl,
    cssVarLeft: "--sky-hc-left",
    cssVarTop: "--sky-hc-top",
    cssVarWidth: "--sky-hc-width",
    getPlacement: () => "bottom" as any,
    getAlign: () => "center" as any,
    getGap: () => 4,
    getPadding: () => 8,
    getMatchAnchorWidth: () => false,
    getMaxWidthToViewport: () => true,
    getMaxWidthMode: () => "cap" as any,
    getFlip: () => true,
    getFlipOrder: () => [],
    getAnimateReposition: () => true,
    getRepositionMs: () => 120,
    getCloseOnEscape: () => true,
    getCloseOnOutside: () => true,
    getRestoreFocusOnClose: () => false,
    onOpenChange: (open) => {
      this.hiddenChipsOpen = open;

      // if opening hidden chips, close main dropdown
      if (open && this.open) {
        this.dropdownPopover.closePopover("api");
      }
    },
  });


  /** Named prop preset from nearest `sky-config-provider`. */
  @property({ type: String, reflect: true }) preset = "";

  private _presets = new PresetController(this);

  static override styles = css`
    :host {
      display: block;
      font-family: var(--sky-font);
      width: 100%;
      --sky-input-height: 36px;
      --sky-input-height-compact: 28px;
      --sky-input-border-width: 1px;
      --sky-input-pad-x: var(--sky-space-3, 12px);
      --sky-input-pad-x-sm: var(--sky-space-1, 4px);
      --sky-input-pad-x-xs: 0px;
      --sky-input-pad-y: var(--sky-space-1, 4px);
      --sky-input-pad-y-sm: 0px;
      --sky-input-gap: var(--sky-space-1, 4px);
      --sky-input-icon-width: 2rem;
    }

    :host([compact]) {
      --sky-input-pad-x: var(--sky-space-2, 8px);
      --sky-input-pad-x-sm: var(--sky-space-1, 4px);
      --sky-input-icon-width: 1.75rem;
    }

    :host([compact]) .combo-box-container > .label {
      font-size: var(--sky-font-size-xs);
      margin-bottom: var(--sky-space-1, 4px);
      line-height: 1.2;
    }

    :host([compact]) .placeholder-label,
    :host([compact]) .chip[single-select] {
      font-size: var(--sky-font-size-sm);
    }

    :host([compact]) .icon-slot {
      font-size: var(--sky-font-size-lg);
    }

    :host([compact]) .clear-button,
    :host([compact]) .dropdown-button {
      font-size: var(--sky-font-size-md);
    }

    :host([disabled]) {
      pointer-events: none;
      opacity: var(--sky-opacity-disabled, 0.6);
    }

    :host([readonly]) .input-wrapper {
      cursor: default;
    }

    :host([variant="highlight"]) .label {
      color: var(--sky-dropdown-color);
    }

    /* Fieldset / legend */
    :host([variant="fieldset"]) {
      overflow: visible;
    }

    :host([variant="fieldset"]) .combo-box-container {
      overflow: visible;
      /* Room for legend text sitting on the top border — keep off :host */
      padding-block-start: 0.65em;
    }

    :host([variant="fieldset"]) fieldset.input-wrapper {
      display: block;
      height: auto;
      margin: 0;
      min-inline-size: 0;
      min-width: 0;
      padding: 0;
      overflow: visible;
      background: var(--sky-glass-solid);
      box-shadow: none;
    }

    :host([variant="fieldset"]) fieldset.input-wrapper[compact] {
      height: auto;
    }

    :host([variant="fieldset"]) .input-fieldset-body {
      display: flex;
      width: 100%;
      align-items: center;
      box-sizing: border-box;
      height: calc(
        var(--sky-input-height) - 2 * var(--sky-input-border-width)
      );
      min-height: calc(
        var(--sky-input-height) - 2 * var(--sky-input-border-width)
      );
      background: transparent;
      border-radius: inherit;
      flex-wrap: nowrap;
      min-width: 0;
      overflow: hidden;
    }

    :host([variant="fieldset"]) fieldset.input-wrapper[compact] .input-fieldset-body {
      height: calc(
        var(--sky-input-height-compact) - 2 * var(--sky-input-border-width)
      );
      min-height: calc(
        var(--sky-input-height-compact) - 2 * var(--sky-input-border-width)
      );
    }

    /* Multi chips: keep fieldset at input height; center chips in the body */
    :host([variant="fieldset"]) .input-fieldset-body:has(.chip:not([single-select])) {
      height: calc(
        var(--sky-input-height) - 2 * var(--sky-input-border-width)
      );
      min-height: calc(
        var(--sky-input-height) - 2 * var(--sky-input-border-width)
      );
      align-items: center;
      padding-block: 0;
    }

    :host([variant="fieldset"][compact])
      .input-fieldset-body:has(.chip:not([single-select])) {
      height: calc(
        var(--sky-input-height-compact) - 2 * var(--sky-input-border-width)
      );
      min-height: calc(
        var(--sky-input-height-compact) - 2 * var(--sky-input-border-width)
      );
    }

    :host([variant="fieldset"]) .input-fieldset-body:has(.chip:not([single-select])) .field-main,
    :host([variant="fieldset"]) .input-fieldset-body:has(.chip:not([single-select])) .chip-container {
      align-self: stretch;
      height: 100%;
      min-height: 0;
      align-items: center;
    }

    :host([variant="fieldset"]) legend.label {
      float: unset;
      width: auto;
      max-width: calc(100% - 2 * var(--sky-input-pad-x));
      margin-inline-start: var(--sky-input-pad-x);
      margin-block: 0;
      padding-inline: var(--sky-input-pad-x-sm);
      padding-block: 0;
      border: none;
      background: transparent;
      background-color: transparent;
      color: var(--sky-text-secondary);
      font-size: var(--sky-font-size-xs);
      /* Take no in-flow space; inner text is centered on the border notch */
      height: 0;
      line-height: 0;
      overflow: visible;
      white-space: nowrap;
      pointer-events: none;
    }

    :host([variant="fieldset"]) legend.label .legend-text {
      display: inline-block;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      line-height: 1.2;
      transform: translateY(-50%);
    }

    :host([variant="fieldset"]) .combo-box-container:focus-within legend.label,
    :host([variant="fieldset"]) .input-wrapper[open] legend.label {
      color: var(--sky-dropdown-color);
      font-size: var(--sky-font-size-xs);
    }

    :host([variant="fieldset"]) .input-wrapper::after {
      content: none;
      display: none;
    }

    :host([variant="fieldset"]) .input-wrapper[open],
    :host([variant="fieldset"]) .input-wrapper:focus-visible {
      border-color: var(--sky-dropdown-color);
    }

    :host([variant="fieldset"][validationActive]:invalid) .input-wrapper {
      border-color: var(--sky-danger-primary);
    }

    :host([variant="fieldset"][validationActive]:invalid) legend.label {
      color: var(--sky-danger-primary);
    }

    :host([variant="fieldset"][compact]) legend.label,
    :host([variant="fieldset"][compact]) .combo-box-container:focus-within legend.label,
    :host([variant="fieldset"][compact]) .input-wrapper[open] legend.label {
      font-size: var(--sky-font-size-xs);
    }

    /* Inside: label in the wrapper above value row — keep tokens in sync with sky-input */
    :host([variant="inside"]) {
      --sky-input-height-inside: 40px;
      --sky-input-height-inside-compact: 36px;
      --sky-input-inside-label-block: 2px;
      --sky-input-inside-value-min: 1.15em;
    }

    :host([variant="inside"]) .input-wrapper {
      height: auto;
      min-height: var(--sky-input-height-inside);
      align-items: center;
      padding-block: var(--sky-input-pad-y-sm, 0px);
      overflow: hidden;
    }

    :host([variant="inside"]) .input-wrapper[compact] {
      height: auto;
      min-height: var(--sky-input-height-inside-compact);
      padding-block: var(--sky-input-pad-y-sm, 0px);
    }

    :host([variant="inside"]) .input-inside-main {
      flex: 1 1 0%;
      min-width: 0;
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 0;
      padding-block: var(--sky-input-inside-label-block);
      padding-inline: 0;
      overflow: hidden;
    }

    :host([variant="inside"]) .input-inside-main > .label {
      margin: 0;
      padding-inline: var(--sky-input-pad-x);
      font-size: var(--sky-font-size-xs);
      line-height: 1.15;
      min-height: 0;
      color: var(--sky-text-secondary);
      pointer-events: none;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 0 0 auto;
    }

    :host([variant="inside"][compact]) .input-inside-main > .label {
      font-size: 0.6875rem;
      line-height: 1.15;
      min-height: 0;
    }

    :host([variant="inside"]) .combo-box-container:focus-within .input-inside-main > .label,
    :host([variant="inside"]) .input-wrapper[open] .input-inside-main > .label {
      color: var(--sky-dropdown-color);
      font-size: var(--sky-font-size-xs);
    }

    :host([variant="inside"][compact]) .combo-box-container:focus-within .input-inside-main > .label,
    :host([variant="inside"][compact]) .input-wrapper[open] .input-inside-main > .label {
      font-size: 0.6875rem;
    }

    :host([variant="inside"][validationActive]:invalid) .input-inside-main > .label {
      color: var(--sky-danger-primary);
    }

    :host([variant="inside"]) .input-inside-row {
      display: flex;
      align-items: center;
      min-width: 0;
      width: 100%;
      min-height: var(--sky-input-inside-value-min);
      gap: 0;
      overflow: hidden;
      flex: 0 0 auto;
    }

    :host([variant="inside"]) .input-inside-main:has(.chip:not([single-select])) {
      justify-content: center;
      align-self: center;
      padding-block: 0;
      gap: 2px;
    }

    :host([variant="inside"]) .input-inside-row:has(.chip:not([single-select])) {
      min-height: 0;
      height: auto;
      align-items: center;
    }

    :host([variant="inside"]) .field-main:has(.chip:not([single-select])) {
      align-self: center;
      height: auto;
      min-height: 0;
    }

    /* Match sky-input: label + value share pad-x; zero start when left icon present */
    :host([variant="inside"]) .input-inside-main > .label,
    :host([variant="inside"]) .chip-container,
    :host([variant="inside"]) .value-summary,
    :host([variant="inside"]) .placeholder-label {
      padding-inline-start: var(--sky-input-pad-x);
      padding-inline-end: var(--sky-input-pad-x);
    }

    :host([variant="inside"]) .input-wrapper:has(.icon-slot[filled].left) .input-inside-main > .label,
    :host([variant="inside"]) .input-wrapper:has(.icon-slot[filled].left) .chip-container,
    :host([variant="inside"]) .input-wrapper:has(.icon-slot[filled].left) .value-summary,
    :host([variant="inside"]) .input-wrapper:has(.icon-slot[filled].left) .placeholder-label {
      padding-inline-start: 0;
    }

    /* Left icon / trailing chrome centered to label+value column (like sky-input) */
    :host([variant="inside"]) .icon-slot,
    :host([variant="inside"]) .icon-slot[filled] {
      align-self: center;
      height: auto;
      max-height: none;
      align-items: center;
      justify-content: center;
    }

    :host([variant="inside"]) .field-trailing {
      align-self: center;
      padding-inline-start: 0;
    }

    /* Outside labels — keep accent while dropdown is open (focus may leave into popover) */
    .combo-box-container:focus-within > .label,
    .combo-box-container:has(.input-wrapper[open]) > .label {
      color: var(--sky-dropdown-color);
      transition: color 0.3s ease;
    }

    :host([compact]) .combo-box-container:focus-within > .label,
    :host([compact]) .combo-box-container:has(.input-wrapper[open]) > .label {
      font-size: var(--sky-font-size-xs);
    }

    .label {
      font-size: var(--sky-font-size-sm);
      margin-bottom: var(--sky-space-1, 4px);
      text-align: start;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-family: var(--sky-font);
      line-height: 1.35;
    }

    .combo-box-container {
      position: relative;
      display: flex;
      flex-direction: column;
      max-width: 100%;
      overflow: visible;
      width: 100%;
    }

    .combo-box-container.shake {
      animation: shake 0.3s ease-in-out;
      animation-iteration-count: 1;
    }

    @keyframes shake {
      0% {
        transform: translateX(0);
      }
      25% {
        transform: translateX(-3px);
      }
      50% {
        transform: translateX(3px);
      }
      75% {
        transform: translateX(-3px);
      }
      100% {
        transform: translateX(0);
      }
    }

    .search-container {
      display: flex;
      align-items: center;
      padding: 8px 10px;
      gap: var(--sky-input-gap, 4px);
      border-bottom: 1px solid var(--sky-border-light);
      margin-bottom: 5px;
    }

    .search-container .dropdown-search-input {
      flex: 1 1 auto;
      min-width: 0;
      width: 100%;
    }

    .new-option-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 22px;
      height: 22px;
      border-radius: var(--sky-radius-primary);
      margin-left: 6px;
      cursor: pointer;
      background: var(--sky-hover-primary);
      border: var(--sky-border-primary);
      color: var(--sky-dropdown-color);
      flex-shrink: 0;
      transition: color 0.15s ease;
    }
    .new-option-btn:hover {
      color: var(--sky-active-primary);
    }
    .new-option-btn[disabled] {
      opacity: 0.6;
      pointer-events: none;
    }

    .input-wrapper {
      display: flex;
      align-items: center;
      background: var(--sky-glass-solid);
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
      box-sizing: border-box;
      border-radius: var(--sky-radius-secondary);
      height: var(--sky-input-height);
      min-height: var(--sky-input-height);
      width: 100%;
      flex-wrap: nowrap;
      position: relative;
      overflow: hidden;
      border: var(--sky-border-primary);
      box-shadow: var(--sky-box-shadow-primary);
      transition:
        border-color var(--sky-transition-normal, 200ms ease),
        box-shadow var(--sky-transition-normal, 200ms ease);
      flex: 1 1 auto;
      min-width: 0;
    }

    .input-wrapper:focus-visible {
      border-bottom: 1px solid var(--sky-dropdown-color);
      outline: none;
    }

    .input-wrapper::after {
      content: "";
      position: absolute;
      top: -1px;
      left: -1px;
      right: -1px;
      bottom: -1px;
      border-radius: inherit;
      border: 1px solid transparent;
      border-bottom-color: transparent;
      opacity: 0;
      transition:
        opacity 0.3s ease,
        border-color 0.3s ease;
      pointer-events: none;
    }

    .input-wrapper[open]::after {
      opacity: 1;
      border-bottom-color: var(--sky-dropdown-color);
    }

    :host([validationActive]:invalid) .input-wrapper::after {
      opacity: 1;
      border-bottom-color: var(--sky-danger-primary);
    }

    :host([validationActive]:invalid) .label {
      color: var(--sky-danger-primary);
    }

    :host([validationActive]:invalid) .icon-slot {
      color: var(--sky-danger-primary);
    }

    .placeholder-label.active[compact] {
      top: 10px;
      background-color: var(--sky-dropdown-color);
      color: var(--sky-on-color-fallback-light, var(--sky-text-white));
      font-size: var(--sky-font-size-xs);
      padding: 0 5px;
      border-radius: 4px;
    }

    .input-wrapper[compact] {
      height: var(--sky-input-height-compact);
      min-height: var(--sky-input-height-compact);
      border-radius: var(--sky-radius-primary);
    }

    /* Multi chips stay single-line; do not grow taller than sky-input.
       Exempt inside variant — it needs two rows (label + value). */
    .input-wrapper:has(.chip:not([single-select])) {
      height: var(--sky-input-height);
      min-height: var(--sky-input-height);
      padding-block: 0;
    }

    .input-wrapper[compact]:has(.chip:not([single-select])) {
      height: var(--sky-input-height-compact);
      min-height: var(--sky-input-height-compact);
      padding-block: 0;
    }

    :host([variant="inside"]) .input-wrapper,
    :host([variant="inside"]) .input-wrapper:has(.chip),
    :host([variant="inside"]) .input-wrapper[compact]:has(.chip) {
      height: auto;
      min-height: var(--sky-input-height-inside);
      padding-block: var(--sky-input-pad-y-sm, 0px);
      align-items: center;
    }

    :host([variant="inside"][compact]) .input-wrapper,
    :host([variant="inside"][compact]) .input-wrapper:has(.chip) {
      min-height: var(--sky-input-height-inside-compact);
    }

    :host([variant="inside"]) .input-wrapper:has(.chip:not([single-select])) {
      padding-block: 4px;
    }

    :host([variant="inside"][compact]) .input-wrapper:has(.chip:not([single-select])) {
      padding-block: 2px;
    }

    :host([compact]) .placeholder-label {
      font-size: var(--sky-font-size-sm);
    }

    sky-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1em;
      height: 1em;
      flex-shrink: 0;
    }

    /* Value column: takes remaining width; never paints under trailing actions */
    .field-main {
      flex: 1 1 0%;
      min-width: 0;
      max-width: 100%;
      overflow: hidden;
      display: flex;
      align-items: center;
      align-self: stretch;
      position: relative;
      z-index: 0;
    }

    .chip-container {
      display: flex;
      gap: var(--sky-input-gap);
      flex: 1 1 0%;
      width: 100%;
      min-width: 0;
      max-width: 100%;
      min-height: 0;
      height: 100%;
      flex-wrap: nowrap;
      align-items: center;
      align-self: stretch;
      overflow: hidden;
      box-sizing: border-box;
      margin: 0;
      line-height: 1;
      padding-block: 4px;
      padding-inline: var(--sky-input-pad-x) var(--sky-input-pad-x-sm);
    }

    :host([compact]) .chip-container,
    .chip-container[compact] {
      gap: 4px;
      padding-block: 2px;
    }

    :host([variant="inside"]) .chip-container {
      height: auto;
      align-self: center;
      padding-block: 0;
    }

    .input-wrapper:has(.icon-slot[filled].left) .chip-container {
      padding-inline-start: var(--sky-input-pad-x-sm);
    }

    .value-summary {
      display: block;
      flex: 1 1 0%;
      width: 100%;
      min-width: 0;
      max-width: 100%;
      box-sizing: border-box;
      margin: 0;
      padding-inline: var(--sky-input-pad-x) var(--sky-input-pad-x-sm);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: var(--sky-font-size-md);
      color: var(--sky-text-primary);
      font-family: var(--sky-font);
      line-height: 1.25;
      text-align: start;
    }

    .input-wrapper:has(.icon-slot[filled].left) .value-summary {
      padding-inline-start: var(--sky-input-pad-x-sm);
    }

    :host([compact]) .value-summary {
      font-size: var(--sky-font-size-sm);
    }

    .chip-container::-webkit-scrollbar {
      display: none;
    }

    .chip {
      background-color: var(--sky-glass-primary);
      border-radius: var(--sky-radius-primary);
      padding: 0 6px;
      height: 1.25rem;
      max-height: calc(var(--sky-input-height) - 10px);
      font-size: var(--sky-font-size-sm);
      line-height: 1;
      display: inline-flex;
      align-items: center;
      align-self: center;
      gap: 4px;
      flex: 0 1 auto;
      max-width: 100%;
      min-width: 0;
      border: var(--sky-border-primary);
      font-family: var(--sky-font);
      text-align: start;
      box-sizing: border-box;
      white-space: nowrap;
    }

    .chip .chip-label {
      min-width: 0;
      flex: 1 1 auto;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      line-height: 1;
    }

    .chip.overflow-chip {
      flex: 0 0 auto;
      white-space: nowrap;
      max-width: none;
    }

    :host([compact]) .chip {
      height: 1.125rem;
      max-height: calc(var(--sky-input-height-compact) - 8px);
      padding: 0 4px;
      font-size: var(--sky-font-size-xs);
    }

    .chip .remove-chip[compact] {
      flex-shrink: 0;
    }
    .remove-chip-inner {
      font-size: var(--sky-font-size-md);
      color: var(--sky-dropdown-color);
      background-color: var(--sky-hover-primary);
      border-radius: var(--sky-radius-full);
      border: none;
      cursor: pointer;
      transition: color 0.2s ease, background-color 0.2s ease;
    }
    .remove-chip-inner:hover {
      color: var(--sky-danger-primary);
    }
    .remove-chip:hover {
      color: var(--sky-danger-primary);
    }

    .chip .remove-chip {
      position: static;
      flex-shrink: 0;
      align-self: center;
      font-size: var(--sky-font-size-sm);
      color: var(--sky-dropdown-color);
      background-color: var(--sky-hover-primary);
      border-radius: var(--sky-radius-full);
      cursor: pointer;
      border: none;
      transition: color 0.2s ease, background-color 0.2s ease;
    }

    .input-wrapper[disabled] {
      opacity: 0.6;
      pointer-events: none;
    }

    .dropdown {
      position: fixed;
      padding: 5px;
      background: var(--sky-glass-tertiary);
      background-image: var(--sky-glass-surface);
      backdrop-filter: var(--sky-blur-tertiary) var(--sky-brightness-tertiary)
        var(--sky-saturation-tertiary);
      -webkit-backdrop-filter: var(--sky-blur-tertiary)
        var(--sky-brightness-tertiary) var(--sky-saturation-tertiary);
      border-radius: var(--sky-radius-secondary);
      box-shadow:
        var(--sky-box-shadow-inset),
        var(--sky-box-shadow-tertiary);
      box-sizing: border-box;
      pointer-events: auto;
      margin: 0;
      border: var(--sky-border-tertiary);
    }

    .dropdown-scroller {
      max-height: var(--sky-scroll-height);
      overflow: auto;
      scrollbar-width: thin;
      scrollbar-color: var(--sky-scrollbar-thumb) var(--sky-scrollbar-track);
    }

    .dropdown-scroller::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }

    .dropdown-scroller::-webkit-scrollbar-thumb {
      background-color: var(--sky-scrollbar-thumb);
      border-radius: var(--sky-radius-full, 9999px);
      transition: background-color var(--sky-duration-normal, 200ms)
        var(--sky-ease-default, ease);
      cursor: grab;
    }

    .dropdown-scroller::-webkit-scrollbar-thumb:hover {
      background-color: var(--sky-scrollbar-thumb-hover);
    }

    .dropdown-scroller::-webkit-scrollbar-track {
      background: var(--sky-scrollbar-track);
    }

    .dropdown-scroller::-webkit-scrollbar-button {
      background-color: transparent;
    }

    .dropdown-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 10px;
      border-radius: var(--sky-radius-secondary);
      cursor: pointer;
      text-align: left;
      font-size: var(--sky-font-size-sm);
      color: var(--sky-text-primary);
      transition:
        background-color 0.15s ease,
        color 0.15s ease;
      box-sizing: border-box;
      margin: 2px 0;
      font-family: var(--sky-font);
    }

    .dropdown-item:hover {
      background-color: var(--sky-hover-primary);
    }

    .dropdown-item.active {
      background-color: var(--sky-hover-primary);
    }

    .dropdown-item.selected {
      color: var(--sky-dropdown-color, var(--sky-active-primary));
      background-color: var(--sky-active-g0, var(--sky-hover-primary));
      font-weight: var(--sky-font-weight-medium, 500);
    }

    .dropdown-item.active.selected,
    .dropdown-item.selected:hover {
      background-color: var(--sky-active-g0, var(--sky-hover-primary));
      color: var(--sky-dropdown-color, var(--sky-active-primary));
    }

    :host([compact]) .dropdown-item {
      font-size: var(--sky-font-size-sm);
      padding: 5px 8px;
      margin: 1px 0;
    }

    .icon-slot {
      display: none;
      justify-content: center;
      font-size: var(--sky-font-size-xl);
      align-items: center;
      align-self: center;
      color: var(--sky-text-primary);
      height: 100%;
      max-height: 100%;
      box-sizing: border-box;
      flex: 0 0 var(--sky-input-icon-width);
      width: var(--sky-input-icon-width);
      min-width: var(--sky-input-icon-width);
      padding: 0;
      background: none;
      border: none;
      transition: color var(--sky-transition-normal, 200ms ease);
    }

    .hidden-chips-dropdown {
      position: fixed; /* important: popover top layer */
      background: var(--sky-glass-tertiary);
      background-image: var(--sky-glass-surface);
      backdrop-filter: var(--sky-blur-tertiary) var(--sky-brightness-tertiary)
        var(--sky-saturation-tertiary);
      -webkit-backdrop-filter: var(--sky-blur-tertiary)
        var(--sky-brightness-tertiary) var(--sky-saturation-tertiary);
      box-shadow:
        var(--sky-box-shadow-inset),
        var(--sky-box-shadow-tertiary);
      border-radius: var(--sky-radius-secondary);
      border: var(--sky-border-tertiary);
      width: max-content;
      opacity: 0;
      transform: scale(0.98);
      transition: opacity 0.15s ease, transform 0.15s ease;
      margin: 0;
    }

    .hidden-chips-dropdown:popover-open {
      opacity: 1;
      transform: scale(1);
    }

    .hidden-chip-inner {
      padding: 4px 8px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      cursor: pointer;
      gap: 5px;
      font-size: var(--sky-font-size-sm);
      border-bottom: var(--sky-border-primary);
      font-family: var(--sky-font);
    }

    .hidden-chip-inner:last-child {
      border-bottom: none;
    }

    .icon-slot[filled] {
      display: flex;
      flex-shrink: 0;
      cursor: pointer;
    }

    .icon-slot[filled]:hover,
    .icon-slot[filled]:active,
    .icon-slot.focused,
    .input-wrapper[open] .icon-slot[filled] {
      color: var(--sky-dropdown-color);
    }

    .placeholder-label {
      white-space: nowrap;
      box-sizing: border-box;
      padding-inline: var(--sky-input-pad-x);
      flex: 1 1 0%;
      width: 100%;
      min-width: 0;
      text-align: start;
      font-size: var(--sky-font-size-md);
      line-height: 1.25;
      color: var(--sky-text-tertiary);
      transition: color var(--sky-transition-normal, 200ms ease);
      pointer-events: none;
      overflow: hidden;
      text-overflow: ellipsis;
      font-family: var(--sky-font);
      display: flex;
      align-items: center;
      align-self: stretch;
    }

    .input-wrapper:has(.icon-slot[filled].left) .placeholder-label {
      padding-inline-start: var(--sky-input-pad-x-sm);
    }

    .placeholder-label.active {
      display: none;
    }

    .field-trailing {
      display: flex;
      flex: 0 0 auto;
      flex-shrink: 0;
      align-items: center;
      justify-content: flex-end;
      align-self: stretch;
      gap: var(--sky-input-pad-x-sm);
      padding-inline-end: var(--sky-input-pad-x-sm);
      padding-inline-start: var(--sky-input-pad-x-sm);
      box-sizing: border-box;
    }

    sky-icon.clear-button {
      box-sizing: border-box;
      flex: 0 0 auto;
      width: 1.125rem;
      height: 1.125rem;
      min-width: 1.125rem;
      min-height: 1.125rem;
      margin: 0;
      padding: 0;
      border: none;
      font-size: 0.75rem;
      line-height: 1;
      color: var(--sky-dropdown-color);
      background-color: var(--sky-hover-primary);
      border-radius: var(--sky-radius-full);
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
      cursor: pointer;
      transition: color var(--sky-transition-normal, 200ms ease);
    }

    sky-icon.clear-button[compact] {
      width: 1rem;
      height: 1rem;
      min-width: 1rem;
      min-height: 1rem;
      font-size: 0.6875rem;
    }

    sky-icon.clear-button:hover {
      color: var(--sky-danger-primary);
    }

    sky-icon.dropdown-button {
      box-sizing: border-box;
      flex: 0 0 auto;
      width: var(--sky-input-icon-width);
      height: auto;
      min-width: calc(var(--sky-input-icon-width) - 0.25rem);
      margin: 0;
      padding: 0;
      border: none;
      font-size: var(--sky-font-size-sm);
      color: var(--sky-text-primary);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: color var(--sky-transition-normal, 200ms ease);
      background: none;
    }

    sky-icon.dropdown-button:hover,
    .input-wrapper[open] sky-icon.dropdown-button {
      color: var(--sky-dropdown-color);
    }

    sky-icon.dropdown-button[compact] {
      font-size: var(--sky-font-size-md);
    }

    .loading-indicator {
      flex-shrink: 0;
      margin-inline-end: var(--sky-input-pad-x-sm);
      width: 16px;
      height: 16px;
      border: 2px solid var(--sky-border-light, var(--sky-text-tertiary));
      border-top: 2px solid var(--sky-dropdown-color, var(--sky-active-primary));
      border-radius: var(--sky-radius-full);
      animation: spin 1s linear infinite;
    }

    ::slotted(.icon-slot) {
      display: block;
    }

    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }

    .highlight {
      color: var(--sky-dropdown-color);
      font-weight: var(--sky-font-weight-semibold);
    }

    .progress-container {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;
      position: absolute;
      width: 100%;
      top: 100%;
    }

    .progress-message {
      pointer-events: none;
      width: 100%;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      font-size: var(--sky-font-size-sm);
      margin-inline-start: var(--sky-input-pad-x-sm);
      font-family: var(--sky-font);
    }

    :dir(rtl) .progress-message {
      margin-inline-start: unset;
      margin-inline-end: var(--sky-input-pad-x-sm);
    }

    .error-list {
      list-style: none;
      margin: 0;
      padding: 0;
      width: 100%;
    }

    .error {
      font-size: var(--sky-font-size-xs);
      color: var(--sky-danger-primary);
      text-align: start;
      white-space: nowrap;
      text-overflow: ellipsis;
      font-family: var(--sky-font);
    }
    .sky-checkbox {
      appearance: none;
      flex-shrink: 0;
      width: 16px;
      height: 16px;
      border-radius: 6px;
      margin-right: 5px;
      border: 1px solid var(--sky-text-tertiary);
      background-color: transparent;
      box-shadow: none;
      cursor: pointer;
      position: relative;
      transition:
        background-color 0.3s ease,
        border-color 0.3s ease,
        box-shadow 0.3s ease;
    }

    .sky-checkbox:hover {
      background-color: var(--sky-text-tertiary);
      border-color: var(--sky-text-tertiary);
    }

    .sky-checkbox:focus-visible {
      background-color: var(--sky-text-tertiary);
      border-color: var(--sky-text-tertiary);
      outline: none;
    }

    .sky-checkbox:checked {
      background-color: var(--sky-active-primary);
      border-color: var(--sky-active-primary);
    }

    .sky-checkbox::after {
      content: "";
      width: 8px;
      height: 4px;
      border: 2px solid var(--sky-on-color-fallback-light, var(--sky-text-white));
      border-top: none;
      border-right: none;
      position: absolute;
      top: 6px;
      left: 50%;
      transform: translate(-50%, -50%) rotate(-90deg) scale(0.8);
      opacity: 0;
      transition:
        opacity 0.2s ease-in-out,
        transform 0.2s ease-in-out;
    }

    .sky-checkbox.indeterminate {
      background-color: var(--sky-active-primary);
      border-color: var(--sky-active-primary);
    }

    .sky-checkbox.indeterminate::after {
      width: 8px;
      height: 2px;
      background-color: var(--sky-on-color-fallback-light, var(--sky-text-white));
      border: none;
      top: 50%;
      left: 50%;
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }

    .sky-checkbox:checked::after {
      opacity: 1;
      transform: translate(-50%, -50%) rotate(-45deg) scale(1);
    }

    .sky-checkbox:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    ${unsafeCSS(
      cssOnFillEnhance([
        {
          selector: ".placeholder-label.active[compact]",
          fillVar: "--sky-dropdown-color",
        },
        {
          selector: ".sky-checkbox:checked::after",
          fillVar: "--sky-active-primary",
          property: "border-color",
        },
        {
          selector: ".sky-checkbox.indeterminate::after",
          fillVar: "--sky-active-primary",
          property: "background-color",
        },
      ]),
    )}
  `;

  constructor() {
    super();
    this.captureDefaultValue("");
  }

  static override shadowRootOptions = {
    ...LitElement.shadowRootOptions,
    delegatesFocus: true,
  };

  override connectedCallback() {
    super.connectedCallback();

    if (!this.hasAttribute("tabindex")) {
      this.tabIndex = this.disabled ? -1 : 0;
    }
    this.refreshOptionSlots();

  }

  override firstUpdated() {
    this.dropdownPopover.refreshTargets();
    this.hiddenPopover.refreshTargets();
  }

  private refreshOptionSlots = () => {
    const set = new Set<string>();
    this.querySelectorAll<HTMLElement>('[slot^="option-"]').forEach((el) => {
      const slotName = (el as HTMLElement).slot;
      if (slotName) set.add(slotName);
    });
    this.optionSlotSet = set;
  };

  /** Get the current value for form submission */
  protected override getFormValue(): FormState {
    if (this.disabled || !this.name) return null;

    if (this.multiSelect) {
      const formData = new FormData();
      this.selectedItems.forEach((item) => {
        const value = this.emitFullObject
          ? JSON.stringify(item)
          : String(this.getOptionValue(item));
        formData.append(this.name, value);
      });
      // Add a dummy input when nothing is selected (for form validation)
      if (this.selectedItems.length === 0 && this.required) {
        formData.append(this.name, "");
      }
      return formData;
    } else {
      if (this.selectedItems.length === 0) return "";
      const item = this.selectedItems[0];
      return this.emitFullObject
        ? JSON.stringify(item)
        : String(this.getOptionValue(item));
    }
  }

  /** Set value from form state (e.g., form reset) */
  protected override setValueFromFormState(state: FormState): void {
    if (state === null || state === "") {
      this.value = this.multiSelect ? [] : "";
      this.selectedItems = [];
      return;
    }

    if (this.multiSelect) {
      let values: any[] = [];

      if (state instanceof FormData) {
        values = state.getAll(this.name);
      } else if (Array.isArray(state)) {
        values = state;
      } else if (typeof state === "string") {
        try {
          const parsed = JSON.parse(state);
          if (Array.isArray(parsed)) {
            values = parsed;
          } else {
            values = [state];
          }
        } catch {
          values = [state];
        }
      }

      this.setValueFromArray(values);
    } else {
      let stringValue = "";
      if (typeof state === "string") {
        stringValue = state;
      } else if (state instanceof FormData) {
        stringValue = state.get(this.name)?.toString() || "";
      } else if (Array.isArray(state) && state.length > 0) {
        stringValue = state[0].toString();
      }

      this.value = stringValue;
      this.syncSelectedItemsWithValue();
    }
  }
  protected override syncFormValue(): void {
    if (!this.name || this.disabled) {
      this.internals.setFormValue(null);
      return;
    }

    const v = this.getFormValue();

    if (v === null) {
      this.internals.setFormValue(null);
      return;
    }

    if (this.multiSelect && Array.isArray(v)) {
      // Matches native <select multiple> form value shape.
      const formData = new FormData();
      v.forEach((value) => {
        if (value !== null && value !== undefined && value !== "") {
          formData.append(this.name, String(value));
        }
      });
      this.internals.setFormValue(formData);
    } else {
      this.internals.setFormValue(v as string | File | null);
    }
  }
  private setValueFromArray(values: any[]) {
    const optionsArray = Array.isArray(this.options) ? this.options : [];

    this.selectedItems = optionsArray.filter((option) => {
      const optionValue = this.getOptionValue(option);
      const optionValueString = this.emitFullObject
        ? JSON.stringify(option)
        : String(optionValue);

      return values.some((value) => {
        const stringValue = String(value);

        if (this.emitFullObject) {
          try {
            const parsedValue = JSON.parse(stringValue);
            return JSON.stringify(parsedValue) === optionValueString;
          } catch {
            return stringValue === optionValueString;
          }
        } else {
          return stringValue === optionValueString;
        }
      });
    });

    this.value = this.selectedItems.map((item) =>
      this.emitFullObject ? item : this.getOptionValue(item),
    );
  }

  /** Get the validity anchor (for validation messages) */
  protected override getValidityAnchor(): HTMLElement | undefined {
    return this;
  }

  /** Check if empty (for required validation) */
  protected override isEmpty(): boolean {
    if (this.multiSelect) {
      return this.selectedItems.length === 0;
    } else {
      return this.selectedItems.length === 0 || !this.value;
    }
  }

  protected override getNativeControl(): HTMLElement | null {
    return this.inputWrapperEl || null;
  }
  /** Override to provide custom error messages */
  protected override getCustomErrorMessage(): string {
    return this.validationErrors?.[0] || "";
  }

  /** Override form reset callback */
  protected override onFormReset(): void {
    super.onFormReset();
    this.searchQuery = "";
    this.emitSearchQueryChanged();
    this.open = false;
    this.invalid = false;
    this.validationErrors = [];
    this.validationActive = false;
  }

  /** Override form disabled callback */
  protected override onFormDisabled(disabled: boolean): void {
    this.disabled = disabled;
  }

  /** Override form state restore callback */
  protected override onFormStateRestore(state: FormState): void {
    this.setValueFromFormState(state);
  }


  private toggleSelectAll(): void {
    if (this.disabled || this.readonly) return;

    if (this.isAllFilteredSelected()) {
      const filteredKeys = new Set(
        this.filteredOptions.map((opt) => this.optionKey(opt.original)),
      );

      const nextSelected = this.selectedItems.filter(
        (item) => !filteredKeys.has(this.optionKey(item)),
      );

      // 🚫 prevent going below 1 selection when enforced
      if (this.enforceMinOneSelection && nextSelected.length === 0) {
        return;
      }

      this.selectedItems = nextSelected;
    } else {
      const selectedKeys = new Set(
        this.selectedItems.map((item) => this.optionKey(item)),
      );
      const additions = this.filteredOptions
        .map((o) => o.original)
        .filter((opt) => !selectedKeys.has(this.optionKey(opt)));
      this.selectedItems = [...this.selectedItems, ...additions];
    }

    const valuesArray = this.selectedItems.map((opt) =>
      this.emitFullObject ? opt : this.getOptionValue(opt),
    );
    this.value = valuesArray;

    this.dispatchEvent(
      new CustomEvent<SkyComboboxValueChangedDetail>("value-changed", {
        detail: {
          value: valuesArray,
          selectedItems: [...this.selectedItems],
          emittedValues: valuesArray,
          isFilteredSelection: !!this.searchQuery.trim(),
        },
        bubbles: true,
        composed: true,
      }),
    );

    this.syncFormValue();
    this.syncValidity();
    this.requestUpdate();
  }

  private isAllFilteredSelected(): boolean {
    if (this.filteredOptions.length === 0) return false;

    const selectedKeys = new Set(
      this.selectedItems.map((item) => this.optionKey(item)),
    );
    return this.filteredOptions.every((opt) =>
      selectedKeys.has(this.optionKey(opt.original)),
    );
  }

  private truncateText(text: string, maxLength: number): string {
    if (text.length > maxLength) {
      return text.slice(0, maxLength) + "...";
    }
    return text;
  }

  private validateInput(): void {
    if (!this.validationActive) return;

    const nextErrors: string[] = [];
    const target = this.validationArrayFormat ? this.selectedItems : this.value;

    // 1) required first (single source of truth inside component UI)
    if (this.required && this.isEmpty()) {
      nextErrors.push(this.requiredMessage || "Selection is required.");
    } else if (Array.isArray(this.validations) && this.validations.length) {
      // 2) run external validations only when not empty (optional but cleaner)
      for (const v of this.validations) {
        const res = v(target);
        if (res !== true) nextErrors.push(String(res || "Invalid value."));
      }
    }

    const nextInvalid = nextErrors.length > 0;
    const errorsChanged = !samePrimitiveArray(this.validationErrors, nextErrors);
    if (errorsChanged) this.validationErrors = nextErrors;
    if (this.invalid !== nextInvalid) this.invalid = nextInvalid;

    this.dispatchEvent(
      new CustomEvent<SkyComboboxValidationErrorDetail | SkyComboboxValidationSuccessDetail>(
        this.invalid ? "validation-error" : "validation-success",
        {
          detail: this.invalid
            ? { errors: this.validationErrors }
            : { value: this.selectedItems },
          bubbles: true,
          composed: true,
        },
      ),
    );

    this.syncValidity();
  }

  // Blocks form submit when invalid, even if validationActive is false.
  protected override validateForForm(): string {
    if (this.disabled) return "";

    // What should validators receive?
    const target = this.validationArrayFormat ? this.selectedItems : this.value;

    // 1) external validations first
    if (Array.isArray(this.validations) && this.validations.length) {
      for (const v of this.validations) {
        const res = v(target);
        if (res !== true) return String(res || "Invalid value.");
      }
    }

    // 2) enforceMinOneSelection as a hard rule (optional but makes form correct)
    if (this.enforceMinOneSelection && this.isEmpty()) {
      return this.requiredMessage || "At least one selection is required.";
    }

    // 3) required rule
    if (this.required && this.isEmpty()) {
      return this.requiredMessage || "Selection is required.";
    }

    return "";
  }

  override focus(options?: FocusOptions) {
    super.focus(options);
    if (this.searchInputEl) {
      this.searchInputEl.focus(options);
    } else if (this.inputWrapperEl) {
      this.inputWrapperEl.focus(options);
    }
  }

  override blur() {
    if (this.open) {
      this.dropdownPopover.closePopover("light-dismiss");
    }
    super.blur();
  }

  private handleFocus(): void {
    this.focused = true;

    if (!this.validationActive) {
      this.validationActive = true;
    }
  }

  /**
   * Resets combobox state and clears validation UI.
   *
   * @returns {void}
   */
  public reset(): void {
    this.value = this.multiSelect ? [] : "";
    this.selectedItems = [];
    this.searchQuery = "";
    this.emitSearchQueryChanged();
    this.filterOptions();
    this.validationErrors = [];
    this.validationActive = false;
    this.open = false;
    this.invalid = false;
    this.syncFormValue();
    this.syncValidity();
    this.requestUpdate();
  }

  /**
   * Opens the combobox dropdown.
   *
   * @returns {void}
   */
  public openDropdown(): void {
    if (this.disabled || this.readonly) return;
    this.dropdownPopover.openPopover();
  }

  /**
   * Closes the combobox dropdown.
   *
   * @returns {void}
   */
  public closeDropdown(): void {
    this.dropdownPopover.closePopover("api");
  }

  /**
   * Clears current selection when allowed by component settings.
   *
   * @returns {void}
   */
  public clearSelection(): void {
    this.handleClear();
  }

  private handleBlur(): void {
    // Focus often moves into the popover top-layer; keep open chrome while open.
    if (this.open) return;
    this.focused = false;

    if (this.validationActive) {
      this.validateInput();
    }
  }

  override updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has("color")) {
      this.style.setProperty("--sky-dropdown-color", resolveColor(this.color));
    }
    if (changedProperties.has("height")) {
      this.style.setProperty("--sky-scroll-height", this.height);
    }
  }
  override willUpdate(changedProperties: Map<string, unknown>) {
    if (changedProperties.has("customRenderers")) {
      this.syncRendererStateFromProp();
    }

    if (changedProperties.has("value")) {
      this.syncSelectedItemsWithValue();
      this.syncFormValue();
      this.syncValidity();
    }

    if (changedProperties.has("options")) {
      if (!Array.isArray(this.options)) {
        this.options = EMPTY_ARRAY;
      }
      this.syncSelectedItemsWithValue();
      this.filterOptions();
      this.syncFormValue();
      this.syncValidity();
    }

    if (changedProperties.has("searchable")) {
      this.filterOptions();
    }

    if (changedProperties.has("searchQuery") && !this._deferSearchFilter) {
      this.filterOptions();
    }
  }

  /** Merges runtime renderer state (from `customRenderers.$rendererState`) and re-renders. */
  patchRendererState(patch: Record<string, unknown>): void {
    this._rendererRuntimeState = { ...this._rendererRuntimeState, ...patch };
    this.requestUpdate();
  }

  private syncRendererStateFromProp(): void {
    const fromProp = extractComboboxRendererState(this.customRenderers);
    this._rendererRuntimeState = {
      ...this._rendererRuntimeState,
      ...fromProp,
    };
  }

  private rendererStateApi(): SkyComboboxRendererStateApi {
    return {
      rendererState: this._rendererRuntimeState,
      patchRendererState: (patch: Record<string, unknown>) =>
        this.patchRendererState(patch),
    };
  }

  private buildOptionRenderContext(args: {
    option: unknown;
    index: number;
    sourceIndex: number;
    highlightedLabel: string;
    selected: boolean;
    active: boolean;
  }): SkyComboboxOptionRenderContext {
    return {
      index: args.index,
      sourceIndex: args.sourceIndex,
      label: String(this.getOptionLabel(args.option)),
      highlightedLabel: args.highlightedLabel,
      selected: args.selected,
      active: args.active,
      multiSelect: this.multiSelect,
      ...this.rendererStateApi(),
    };
  }

  private buildSelectedRenderContext(
    option: unknown,
    index: number,
  ): SkyComboboxSelectedRenderContext {
    return {
      selectedItems: [...this.selectedItems],
      index,
      multiSelect: this.multiSelect,
      multiDisplay: this.resolveMultiDisplay(),
      label: String(this.getOptionLabel(option)),
      ...this.rendererStateApi(),
    };
  }

  private renderOptionContent(args: {
    option: unknown;
    index: number;
    sourceIndex: number;
    highlighted: string;
    selected: boolean;
    active: boolean;
    hasSlot: boolean;
    slotName: string;
  }) {
    const defaultLabel = html`<span
      class="highlighted-text"
      part="highlighted-text"
      >${unsafeHTML(args.highlighted)}</span
    >`;

    // Named slots win over customRenderers (DOM composition).
    if (args.hasSlot) {
      return html`
        <div style="display: flex; gap: 8px;width:100%">
          <slot name=${args.slotName}> ${defaultLabel} </slot>
        </div>
      `;
    }

    const customRenderer = this.customRenderers?.option;
    if (customRenderer) {
      const content = customRenderer(
        args.option,
        this.buildOptionRenderContext({
          option: args.option,
          index: args.index,
          sourceIndex: args.sourceIndex,
          highlightedLabel: args.highlighted,
          selected: args.selected,
          active: args.active,
        }),
      );
      return html`<div style="display: flex; gap: 8px;width:100%">
        ${content}
      </div>`;
    }

    return defaultLabel;
  }

  private renderSelectedLabel(option: unknown, index: number) {
    const customRenderer = this.customRenderers?.selected;
    if (customRenderer) {
      return customRenderer(option, this.buildSelectedRenderContext(option, index));
    }
    return this.truncateText(String(this.getOptionLabel(option)), this.chipSize);
  }

  private renderSelectedSummaryLabel(option: unknown, index: number) {
    const customRenderer = this.customRenderers?.selected;
    if (customRenderer) {
      return customRenderer(option, this.buildSelectedRenderContext(option, index));
    }
    return String(this.getOptionLabel(option));
  }

  private syncSelectedItemsWithValue(): void {
    const optionsArray = Array.isArray(this.options) ? this.options : [];

    // Treat null/undefined or [] (when multi) as empty
    const isEmptyMulti =
      this.multiSelect && Array.isArray(this.value) && this.value.length === 0;

    if (this.value == null || this.value === "" || isEmptyMulti) {
      this.selectedItems = [];
      this.value = this.multiSelect ? [] : "";
      return;
    }

    // Back-compat: if someone still passes a JSON stringified array
    if (this.multiSelect && typeof this.value === "string") {
      try {
        const parsed = JSON.parse(this.value);
        if (Array.isArray(parsed)) this.value = parsed;
      } catch (_) {
        // ignore non-JSON strings
      }
    }

    if (this.multiSelect) {
      // Normalize incoming to an array
      const raw = Array.isArray(this.value) ? this.value : [this.value];

      // Build a comparable key set from provided values
      const providedKeys = new Set(raw.map((v) => this.optionKey(v)));

      this.selectedItems = optionsArray.filter((opt) =>
        providedKeys.has(this.optionKey(opt)),
      );

      // Normalize this.value to the canonical emitted values
      this.value = this.selectedItems.map((opt) =>
        this.emitFullObject ? opt : this.getOptionValue(opt),
      );
    } else {
      const targetValue = this.optionKey(this.value);

      const selectedOption = optionsArray.find(
        (opt) => this.optionKey(opt) === targetValue,
      );

      this.selectedItems = selectedOption ? [selectedOption] : [];
      this.value = selectedOption
        ? this.emitFullObject
          ? selectedOption
          : this.getOptionValue(selectedOption)
        : "";
    }
  }

  private filterOptions(): void {
    const optionsArray = Array.isArray(this.options) ? this.options : [];

    const makeRow = (option: any, idx: number) => {
      const label = this.getOptionLabel(option);
      return {
        original: option,
        _idx: idx,
        label,
        highlighted: label,
      };
    };

    if (!this.searchable || !this.searchQuery.trim()) {
      this.filteredOptions = optionsArray.map(makeRow);
      return;
    }

    const query = this.searchQuery.toLowerCase();

    this.filteredOptions = optionsArray
      .map(makeRow)
      .filter((row) => String(row.label).toLowerCase().includes(query))
      .map((row) => {
        const regex = new RegExp(`(${query})`, "gi");
        return {
          ...row,
          highlighted: String(row.label).replace(
            regex,
            (m: string) => `<span class="highlight">${m}</span>`,
          ),
        };
      });
  }


  /** Emit search query for hosts / `v-model:search-query` when `searchQuery` changes outside the search input handler. */
  private emitSearchQueryChanged(): void {
    this.dispatchEvent(
      new CustomEvent<SkyComboboxSearchQueryChangedDetail>("search-query-changed", {
        detail: { searchQuery: this.searchQuery },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleSearchValueChanged(
    event: CustomEvent<{ value: string | number }>,
  ): void {
    event.stopPropagation();
    if (this.disabled || this.readonly) return;
    this.applySearchQuery(String(event.detail?.value ?? ""));
  }

  private handleSearchValueCleared(event: Event): void {
    event.stopPropagation();
    if (this.disabled || this.readonly) return;
    this.applySearchQuery("");
  }

  private applySearchQuery(raw: string): void {
    const optionsLength = Array.isArray(this.options) ? this.options.length : 0;
    const shouldDebounce =
      this.searchable &&
      raw.trim().length > 0 &&
      optionsLength > SkyCombobox._SEARCH_DEBOUNCE_OPTIONS_THRESHOLD;

    if (this._searchDebounceId != null) {
      clearTimeout(this._searchDebounceId);
      this._searchDebounceId = null;
    }

    if (shouldDebounce) {
      this._deferSearchFilter = true;
      this.searchQuery = raw;
      this._searchDebounceId = setTimeout(() => {
        this._searchDebounceId = null;
        this._deferSearchFilter = false;
        this.filterOptions();
        this.emitSearchQueryChanged();
      }, SkyCombobox._SEARCH_DEBOUNCE_MS);
      return;
    }

    this._deferSearchFilter = false;
    this.searchQuery = raw;
    this.filterOptions();
    this.emitSearchQueryChanged();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this._deferSearchFilter = false;
    if (this._searchDebounceId != null) {
      clearTimeout(this._searchDebounceId);
      this._searchDebounceId = null;
    }
    if (this._closeResetId != null) {
      clearTimeout(this._closeResetId);
      this._closeResetId = null;
    }
  }

  private handleClear(): void {
    if (this.disabled || this.readonly) return;

    const hasSelection = this.multiSelect
      ? this.selectedItems.length > 0
      : !!this.value;

    if (this.enforceMinOneSelection && hasSelection) {
      return;
    }
    this.value = this.multiSelect ? [] : "";
    this.selectedItems = [];
    this.searchQuery = "";
    this.emitSearchQueryChanged();
    this.open = false;

    this.dispatchEvent(
      new CustomEvent<SkyComboboxValueChangedDetail>("value-changed", {
        detail: {
          value: this.value,
          selectedItems: [],
          isFilteredSelection: false,
        },
        bubbles: true,
        composed: true,
      }),
    );

    this.syncFormValue();
    this.syncValidity();
  }

  selectOption(option: any) {
    if (this.disabled || this.readonly) return;

    const getEmittedValue = (item: any) => {
      return this.emitFullObject ? item : this.getOptionValue(item);
    };

    const wasFiltered = this.searchQuery.trim() !== "";

    if (!this.multiSelect) {
      const alreadySelected =
        this.selectedItems.length &&
        this.optionKey(this.selectedItems[0]) === this.optionKey(option);

      if (alreadySelected && this.enforceMinOneSelection) {
        this.open = false;
        return;
      }

      if (alreadySelected) {
        this.selectedItems = [];
        this.value = "";
      } else {
        this.selectedItems = [option];
        this.value = getEmittedValue(option);
      }

      this.validateInput();

      this.dispatchEvent(
        new CustomEvent("value-changed", {
          detail: {
            value: this.value,
            selectedItems: this.selectedItems,
            emittedValues: this.selectedItems.map(getEmittedValue),
            isFilteredSelection: wasFiltered,
          },
          bubbles: true,
          composed: true,
        }),
      );

      // Search reset is deferred in onOpenChange so options stay visible during close animation.
      this.dropdownPopover.closePopover("api");
      this.syncFormValue();
      this.syncValidity();
      this.requestUpdate();
      return;
    }

    const index = this.selectedItems.findIndex(
      (item) => this.optionKey(item) === this.optionKey(option),
    );

    let nextSelected: any[];

    if (index > -1) {
      nextSelected = [
        ...this.selectedItems.slice(0, index),
        ...this.selectedItems.slice(index + 1),
      ];
    } else {
      nextSelected = [...this.selectedItems, option];
    }

    if (this.enforceMinOneSelection && nextSelected.length === 0) {
      return;
    }

    this.selectedItems = nextSelected;
    this.value = nextSelected.map(getEmittedValue);

    this.validateInput();

    this.dispatchEvent(
      new CustomEvent("value-changed", {
        detail: {
          value: this.value,
          selectedItems: this.selectedItems,
          emittedValues: this.value,
          isFilteredSelection: wasFiltered,
        },
        bubbles: true,
        composed: true,
      }),
    );

    this.syncFormValue();
    this.syncValidity();
    this.open = true;
    this.requestUpdate();
  }

  toggleHiddenChips(e: Event) {
    e.stopPropagation();
    e.preventDefault();
    if (this.disabled || this.readonly) return;

    // close main dropdown if open
    if (this.open) {
      this.dropdownPopover.closePopover("api");
    }

    this.hiddenPopover.toggle();
  }


  renderHiddenChipsDropdown() {
    return html`
    <div
      class="hidden-chips-dropdown"
      part="hidden-chips-dropdown"
      popover="manual"
 @toggle=${this.hiddenPopover.onToggle}
      @click=${(e: MouseEvent) => e.stopPropagation()}
      style="
        left: var(--sky-hc-left, 0px);
        top: var(--sky-hc-top, 0px);
        width: var(--sky-hc-width, max-content);
        z-index: ${this.zIndex};
      "
    >
      ${repeat(
      this.selectedItems.slice(this.visibleChips),
      (item) => this.optionKey(item),
      (item) => html`
          <div class="hidden-chip-inner" part="hidden-chip-inner">
            <span part="hidden-chip-label">
              ${this.renderSelectedLabel(
                item,
                this.selectedItems.indexOf(item),
              )}
            </span>
            <sky-icon
              part="remove-chip-icon"
              class="remove-chip-inner"
              icon="ion:close"
              @click=${(e: MouseEvent) => {
          e.stopPropagation();
          this.removeChip(item);
        }}
            ></sky-icon>
          </div>
        `,
    )}
    </div>
  `;
  }

  removeChip(option: any) {
    if (this.disabled || this.readonly) return;

    const next = this.selectedItems.filter(
      (item) => this.optionKey(item) !== this.optionKey(option),
    );

    // 🚫 prevent going below 1 selection when enforced
    if (this.enforceMinOneSelection && next.length === 0) {
      return;
    }

    this.selectedItems = next;
    if (this.selectedItems.length <= this.visibleChips) {
      this.hiddenPopover.closePopover("api");
    }

    if (this.multiSelect) {
      const valuesArray = this.selectedItems.map((opt) =>
        this.emitFullObject ? opt : this.getOptionValue(opt),
      );
      this.value = valuesArray;

      this.dispatchEvent(
        new CustomEvent("value-changed", {
          detail: {
            value: valuesArray,
            selectedItems: [...this.selectedItems],
            emittedValues: valuesArray,
            isFilteredSelection: false,
          },
          bubbles: true,
          composed: true,
        }),
      );
    } else {
      this.value = "";
      this.dispatchEvent(
        new CustomEvent("value-changed", {
          detail: {
            value: this.value,
            selectedItems: [],
            isFilteredSelection: false,
          },
          bubbles: true,
          composed: true,
        }),
      );
    }

    this.syncFormValue();
    this.syncValidity();
    this.validateInput();
    this.requestUpdate();
  }

  renderChips() {
    // Multi-select chips only — single-select uses value-summary (no chip padding)
    const visible = Array.isArray(this.selectedItems)
      ? this.selectedItems.slice(0, this.visibleChips)
      : [];
    const overflow = this.selectedItems.length - this.visibleChips;
    return html`
      ${repeat(
      visible,
      (item) => this.optionKey(item),
      (item, index) => html`
          <span class="chip" ?compact=${this.compact} part="chip">
            <span class="chip-label" part="chip-label"
              >${this.renderSelectedLabel(item, index)}</span
            >
            ${when(
        this.multiSelect,
        () => html`
                  <sky-icon
                    class="remove-chip"
                    part="remove-chip-icon"
                    ?compact=${this.compact}
                    @click=${(e: MouseEvent) => {
          e.stopPropagation();
          this.removeChip(item);
        }}
                    icon="ion:close"
                  ></sky-icon>
                `,
      )}
          </span>
        `,
    )}
      ${when(
      overflow > 0,
      () => html`
            <span
              class="chip overflow-chip"
              part="overflow-chip"
              style="cursor:pointer"
              ?compact=${this.compact}
              @click=${this.toggleHiddenChips}
            >
              + ${overflow}
            </span>
          `,
    )}
    `;
  }

  private resolveMultiDisplay(): SkyComboboxMultiDisplay {
    const mode = this.multiDisplay;
    if (mode === "text" || mode === "count" || mode === "chips") return mode;
    return "chips";
  }

  /** Selected value area: chips, joined text, or count summary. */
  private renderSelectedValue() {
    if (!this.selectedItems.length) return null;

    // Single-select: plain text (no chip chrome / padding)
    if (!this.multiSelect) {
      const item = this.selectedItems[0];
      const label = String(this.getOptionLabel(item));
      return html`
        <span
          class="value-summary"
          part="value-summary single-select-chip"
          title=${label}
          >${this.renderSelectedSummaryLabel(item, 0)}</span
        >
      `;
    }

    const mode = this.resolveMultiDisplay();

    if (mode === "text") {
      const customRenderer = this.customRenderers?.selected;
      if (customRenderer) {
        return html`
          <span class="value-summary" part="value-summary">
            ${this.selectedItems.map(
              (item, index) => html`${index > 0 ? ", " : ""}${customRenderer(
                item,
                this.buildSelectedRenderContext(item, index),
              )}`,
            )}
          </span>
        `;
      }
      const joined = this.selectedItems
        .map((item) => String(this.getOptionLabel(item)))
        .join(", ");
      return html`
        <span class="value-summary" part="value-summary" title=${joined}
          >${joined}</span
        >
      `;
    }

    if (mode === "count") {
      const n = this.selectedItems.length;
      const first = String(this.getOptionLabel(this.selectedItems[0]));
      const summary = n === 1 ? first : `${first} +${n - 1}`;
      const title = this.selectedItems
        .map((item) => String(this.getOptionLabel(item)))
        .join(", ");
      const customRenderer = this.customRenderers?.selected;
      if (customRenderer) {
        return html`
          <span class="value-summary" part="value-summary" title=${title}>
            ${customRenderer(
              this.selectedItems[0],
              this.buildSelectedRenderContext(this.selectedItems[0], 0),
            )}${n > 1 ? ` +${n - 1}` : nothing}
          </span>
        `;
      }
      return html`
        <span class="value-summary" part="value-summary" title=${title}
          >${summary}</span
        >
      `;
    }

    return html`
      <div class="chip-container" part="chip-container" ?compact=${this.compact}>
        ${this.renderChips()}
      </div>
    `;
  }

  handleKeydown(event: KeyboardEvent) {
    if ((event.target as HTMLElement)?.closest(".search-container")) return;
    const { key, shiftKey } = event;

    if (!this.open && (key === "ArrowDown" || key === "ArrowUp" || key === "Enter")) {
      this.dropdownPopover.openPopover();
      event.preventDefault();
      return;
    }

    if (!this.open) return;

    const hasSearchRow = this.searchable || this.addOption;
    const firstSelectableIndex =
      this.multiSelect && this.selectAll
        ? hasSearchRow
          ? 1
          : 0
        : this.headerRows;

    const minIndex = firstSelectableIndex;
    const maxIndex =
      this.headerRows + Math.max(0, this.filteredOptions.length) - 1;

    if (this.activeIndex < minIndex) this.activeIndex = minIndex;

    if (key === "ArrowDown") {
      const was = this.activeIndex < minIndex ? minIndex : this.activeIndex;
      this.activeIndex = was >= maxIndex ? minIndex : was + 1;
      this.scrollActiveItemIntoView();
      event.preventDefault();
      return;
    }

    if (key === "ArrowUp") {
      const was = this.activeIndex < minIndex ? minIndex : this.activeIndex;
      this.activeIndex = was <= minIndex ? maxIndex : was - 1;
      this.scrollActiveItemIntoView();
      event.preventDefault();
      return;
    }

    if (key === "Tab") {
      if (shiftKey) {
        const was = this.activeIndex < minIndex ? minIndex : this.activeIndex;
        this.activeIndex = was <= minIndex ? maxIndex : was - 1;
      } else {
        const was = this.activeIndex < minIndex ? minIndex : this.activeIndex;
        this.activeIndex = was >= maxIndex ? minIndex : was + 1;
      }
      this.scrollActiveItemIntoView();
      event.preventDefault();
      return;
    }

    if (key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      this.commitDropdownEnter();
      return;
    }

    if (this.readonly) {
      if (key === "Escape") {
        this.open = false;
        event.preventDefault();
      }
      return;
    }

    if (key === "Home") {
      this.activeIndex = minIndex;
      this.scrollActiveItemIntoView();
      event.preventDefault();
      return;
    }

    if (key === "End") {
      this.activeIndex = Math.max(minIndex, maxIndex);
      this.scrollActiveItemIntoView();
      event.preventDefault();
      return;
    }
  }

  private onSearchKeydown = (e: KeyboardEvent) => {
    const { key, shiftKey } = e;

    if (key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      this.commitDropdownEnter();
      return;
    }

    if (key === "ArrowDown" || key === "ArrowUp" || key === "Tab") {
      e.preventDefault();
      e.stopPropagation();

      const hasSearchRow = this.searchable || this.addOption;
      const firstSelectableIndex =
        this.multiSelect && this.selectAll
          ? hasSearchRow
            ? 1
            : 0
          : this.headerRows;

      const minIndex = firstSelectableIndex;
      const maxIndex =
        this.headerRows + Math.max(0, this.filteredOptions.length) - 1;

      const dir = key === "ArrowUp" || (key === "Tab" && e.shiftKey) ? -1 : 1;
      if (this.activeIndex < minIndex) this.activeIndex = minIndex;
      else
        this.activeIndex =
          this.activeIndex + dir < minIndex
            ? maxIndex
            : this.activeIndex + dir > maxIndex
              ? minIndex
              : this.activeIndex + dir;

      this.scrollActiveItemIntoView();
    }

    if (key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      this.dropdownPopover.closePopover("escape");
      return;
    }
  };

  /** Number of non-option rows before the first option (search + select-all). */
  private get headerRows(): number {
    let n = 0;
    if (this.searchable || this.addOption) n += 1;
    if (this.multiSelect && this.selectAll) n += 1;
    return n;
  }


  private getOptionByActiveIndex(): {
    kind: "search" | "select-all" | "option";
    optionIndex?: number;
  } | null {
    if (!this.open) return null;
    const idx = this.activeIndex;
    if (idx < 0) return null;

    if (this.searchable || this.addOption) {
      if (idx === 0) return { kind: "search" };
    }

    const selectAllBase = this.searchable || this.addOption ? 1 : 0;
    if (this.multiSelect && this.selectAll && idx === selectAllBase) {
      return { kind: "select-all" };
    }

    const optionIndex = idx - this.headerRows;
    if (optionIndex >= 0 && optionIndex < this.filteredOptions.length) {
      return { kind: "option", optionIndex };
    }
    return null;
  }

  /**
   * Commits the current keyboard highlight on Enter (search row vs select-all vs option).
   * Shared by {@link handleKeydown} and {@link onSearchKeydown} so focus in the search
   * field matches the same semantics as the input wrapper.
   */
  private commitDropdownEnter(): void {
    const target = this.getOptionByActiveIndex();
    if (!target) return;

    if (target.kind === "search") {
      if (this.addOption && this.canAddaddOption()) {
        this.dispatchEvent(
          new CustomEvent("add-new-option", {
            detail: { value: this.searchQuery.trim() },
            bubbles: true,
            composed: true,
          }),
        );
        this.searchQuery = "";
        this.emitSearchQueryChanged();
        this.filterOptions();
      } else if (this.filteredOptions.length > 0) {
        this.selectOption(this.filteredOptions[0].original);
      }
      return;
    }
    if (target.kind === "select-all") {
      this.toggleSelectAll();
      return;
    }
    if (target.kind === "option" && typeof target.optionIndex === "number") {
      const selectedOption =
        this.filteredOptions[target.optionIndex].original;
      this.selectOption(selectedOption);
    }
  }

  /** Focus logic when opening the dropdown. */
  private focusOnOpen() {
    const search = this.searchInputEl;
    if (search && document.activeElement !== search) {
      requestAnimationFrame(() => search.focus());
    }

    // Highlight first selected option (or first option), not the search row —
    // so selected/active chrome is visible as soon as the list opens.
    if (this.filteredOptions.length > 0) {
      let optionIdx = 0;
      if (this.selectedItems.length > 0) {
        const firstSelKey = this.optionKey(this.selectedItems[0]);
        const idxInFiltered = this.filteredOptions.findIndex(
          (o) => this.optionKey(o.original) === firstSelKey,
        );
        if (idxInFiltered >= 0) optionIdx = idxInFiltered;
      }
      this.activeIndex = this.headerRows + optionIdx;
      this.scrollActiveItemIntoView();
      return;
    }

    this.activeIndex = this.searchable || this.addOption ? 0 : -1;
  }

  private canAddaddOption(): boolean {
    if (!this.addOption) return false;
    const q = this.searchQuery.trim();
    if (!q) return false;

    const optionsArray = Array.isArray(this.options) ? this.options : [];

    const dup = optionsArray.some((opt) => {
      const label = String(this.getOptionLabel(opt) || "").toLowerCase();
      const val = (this.getOptionValue(opt) ?? "").toString().toLowerCase();
      return label === q.toLowerCase() || val === q.toLowerCase();
    });
    return !dup;
  }
  private handleAddaddOption = (e: MouseEvent) => {
    e.stopPropagation();
    if (this.disabled || this.readonly) return;
    if (!this.canAddaddOption()) return;

    const value = this.searchQuery.trim();

    this.dispatchEvent(
      new CustomEvent("add-new-option", {
        detail: { value },
        bubbles: true,
        composed: true,
      }),
    );

    this.searchQuery = "";
    this.emitSearchQueryChanged();
    this.filterOptions();
  };

  scrollActiveItemIntoView() {
    const rows = Array.from(this.rowEls);
    const active = rows.find(
      (r) => Number(r.dataset.rowIndex) === this.activeIndex,
    );
    active?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }

  toggleDropdown(event: MouseEvent) {
    event.stopPropagation();
    if (this.disabled || this.readonly) return;

    // close hidden chips if open
    if (this.hiddenChipsOpen) {
      this.hiddenPopover.closePopover("api");
    }

    this.validationActive = true;
    this.dropdownPopover.toggle();
  }



  renderDropdown() {
    return html`
    <div
      class="dropdown"
      part="dropdown"
      popover="manual"
       @toggle=${this.dropdownPopover.onToggle}
      @click=${(e: MouseEvent) => e.stopPropagation()}
      style="
        left: var(--sky-dd-left, 0px);
        top: var(--sky-dd-top, 0px);
        min-width: var(--sky-dd-width, 100%);
      "
    >
      ${when(
      this.searchable || this.addOption,
      () => html`
            <div
              class="search-container"
              data-row-index="0"
              part="search-container"
            >
              <sky-input
                class="dropdown-search-input"
                compact
                clearable
                part="search-input"
                tabindex="0"
                ?readonly=${this.readonly ||
        (!this.searchable && !this.addOption)}
                ?disabled=${this.disabled}
                placeholder=${this.searchPlaceholder}
                .value=${this.searchQuery}
                @value-changed=${this.searchable || this.addOption
          ? this.handleSearchValueChanged
          : null}
                @value-cleared=${this.searchable || this.addOption
          ? this.handleSearchValueCleared
          : null}
                @keydown=${this.onSearchKeydown}
                @focus=${this.searchable || this.addOption
          ? this.handleFocus
          : null}
                @blur=${this.searchable || this.addOption
          ? this.handleBlur
          : null}
              >
                <sky-icon slot="icon-left" icon="ion:search"></sky-icon>
              </sky-input>

              ${when(
            this.addOption,
            () => html`
                    <sky-icon
                      class="new-option-btn"
                      icon="ion:add"
                      part="add-option-icon"
                      title="Add as new option"
                      aria-label="Add as new option"
                      @click=${this.handleAddaddOption}
                    ></sky-icon>
                  `,
          )}
            </div>
          `,
    )}

<div class="dropdown-scroller">
          ${when(
      this.multiSelect && this.selectAll && this.filteredOptions.length,
      () => {
        const selectAllRowIndex = this.searchable || this.addOption ? 1 : 0;
        const selectAllClasses = classMap({
          "dropdown-item": true,
          active: this.activeIndex === selectAllRowIndex,
          selected: this.isAllFilteredSelected(),
        });
        return html`
                <div
                  class=${selectAllClasses}
                  data-row-index=${selectAllRowIndex}
                  style="justify-content: ${this.justify};"
                  part="select-all"
                  @click=${this.toggleSelectAll}
                >
                  <sky-checkbox
                    .checked=${this.isAllFilteredSelected()}
                    readonly
                  ></sky-checkbox>
                  Select All
                </div>
              `;
      },
    )}
          ${when(
      this.filteredOptions.length > 0,
      () => repeat(
        this.filteredOptions,
        ({ original, _idx }) => `${_idx}-${this.optionKey(original)}`,
        ({ original, highlighted, _idx }, index) => {
          const slotName = `option-${_idx}`;
          const hasSlot = this.optionSlotSet.has(slotName);

          const isSelected =
            Array.isArray(this.selectedItems) &&
            this.selectedItems.some(
              (item) => this.optionKey(item) === this.optionKey(original),
            );
          const rowIndex = this.headerRows + index;
          const isActive = rowIndex === this.activeIndex;
          const itemClasses = classMap({
            "dropdown-item": true,
            active: isActive,
            selected: isSelected,
          });

          return html`
                  <div
                    class=${itemClasses}
                    data-row-index=${rowIndex}
                    style="justify-content: ${this.justify};"
                    part="option"
                    @click=${() => this.selectOption(original)}
                    @pointerenter=${() => {
                      this.activeIndex = rowIndex;
                    }}
                    ?compact=${this.compact}
                  >
                    ${when(
            this.multiSelect,
            () => html` <sky-checkbox
                          .checked=${isSelected}
                          readonly
                        ></sky-checkbox>`,
          )}
                    ${this.renderOptionContent({
                      option: original,
                      index,
                      sourceIndex: _idx,
                      highlighted,
                      selected: isSelected,
                      active: isActive,
                      hasSlot,
                      slotName,
                    })}
                  </div>
                `;
        },
      ),
      () => html`<div
                class="dropdown-item"
                part="no-options"
                data-row-index=${this.headerRows}
              >
                No options found
              </div>`,
    )}
      </div>
    </div>
  `;
  }


  getOptionValue(option: any): any {
    if (this.emitFullObject) {
      return option; // Return the full object
    }

    // When emitFullObject is false, return the emittedKey value
    if (typeof option === "object" && option !== null) {
      // First try the emittedKey
      if (this.emittedKey in option) {
        return option[this.emittedKey];
      }
      // Fallback to any value property
      if ("value" in option) {
        return option.value;
      }
      // Fallback to the object itself
      return option;
    }

    return option;
  }

  /**
   * Stable string key for comparing options / selection identity.
   * Prefer `emittedKey` when present on object options; for other objects use
   * JSON.stringify — never String(object) ("[object Object]"), which makes every
   * option look selected when `emitFullObject` is true.
   */
  private optionKey(option: any): string {
    if (option == null) return String(option);

    if (typeof option === "object") {
      if (this.emittedKey && this.emittedKey in option) {
        const keyVal = option[this.emittedKey];
        return typeof keyVal === "object" && keyVal !== null
          ? JSON.stringify(keyVal)
          : String(keyVal);
      }

      const val = this.getOptionValue(option);
      if (typeof val === "object" && val !== null) {
        return JSON.stringify(val);
      }
      return String(val ?? "");
    }

    return String(option);
  }

  getOptionLabel(option: any): string {
    if (!option) return "";

    const optionsArray = Array.isArray(this.options) ? this.options : [];

    if (
      this.displayKey &&
      typeof option === "object" &&
      option[this.displayKey]
    ) {
      return String(option[this.displayKey]);
    }

    if (typeof option === "object") {
      const stringValues = Object.values(option).filter(
        (value) => typeof value === "string",
      ) as string[];
      return stringValues.length > 0 ? stringValues[0]! : JSON.stringify(option);
    }

    return String(option);
  }

  renderValidationErrors() {
    if (!this.showErrors || !this.validationActive) return null;

    const msgs = this.getErrorMessages();

    if (!msgs.length) return null;

    const errId = `${this.id || "sky-combobox"}-errors`;

    return html`
      <div class="progress-message">
        <ul id=${errId} class="error-list" part="error-list">
          ${repeat(msgs, (m) => m, (m) => html`<li class="error" part="error-item">${m}</li>`)}
        </ul>
      </div>
    `;
  }

  getErrorMessages() {
    const errs = Array.isArray(this.validationErrors)
      ? this.validationErrors
      : [];
    switch (this.errorDisplayMode) {
      case "single":
        return errs.slice(0, 1);
      case "all":
        return errs;
      case "none":
      default:
        return [];
    }
  }

  handleIconClick(position: "left" | "right", event: MouseEvent) {
    this.dispatchEvent(
      new CustomEvent("icon-click", {
        detail: { position, event },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private onIconSlotChange = () => {
    this.hasLeftIcon = (this.iconLeftEls?.length ?? 0) > 0;
    this.hasRightIcon = (this.iconRightEls?.length ?? 0) > 0;
  };

  override render() {
    const leftIconClasses = classMap({ "icon-slot": true, left: true, focused: this.focused });
    const rightIconClasses = classMap({ "icon-slot": true, right: true, focused: this.focused });
    const placeholderClasses = classMap({
      "placeholder-label": true,
      active: this.selectedItems.length > 0,
    });

    const isFieldset = this.variant === "fieldset";
    const isInside = this.variant === "inside";
    const labelEl = this.label
      ? isFieldset
        ? html`<legend class="label" part="label"><span class="legend-text">${this.label}</span></legend>`
        : html`<div class="label" part="label">${this.label}</div>`
      : null;

    const leftIcon = html`
      <div
        class=${leftIconClasses}
        part="icon-slot"
        ?filled=${this.hasLeftIcon}
        @click=${(e: MouseEvent) => this.handleIconClick("left", e)}
      >
        <slot name="icon-left" @slotchange=${this.onIconSlotChange}></slot>
      </div>
    `;

    const valueArea = when(
      this.selectedItems.length > 0,
      () => this.renderSelectedValue(),
      () => html`<label
        class=${placeholderClasses}
        part="placeholder-label"
        ?compact=${this.compact}
      >
        <span style="color:${this.prefixColor}">${this.prefix}</span
        >${this.placeholder}<span style="color:${this.suffixColor}"
          >${this.suffix}</span
        >
      </label>`,
    );

    const trailingActions = html`
      <div class="field-trailing" part="field-trailing">
        ${when(
          this.clearable &&
            !this.enforceMinOneSelection &&
            (this.value || this.selectedItems.length),
          () => html`<sky-icon
            class="clear-button"
            part="clear-button"
            ?compact=${this.compact}
            @click=${(e: MouseEvent) => {
              e.stopPropagation();
              this.handleClear();
            }}
            ?loading=${this.loading}
            icon="ion:close"
          ></sky-icon>`,
        )}
        ${when(
          this.loading,
          () => html`<div class="loading-indicator right"></div>`,
          () =>
            this.hasRightIcon
              ? html`<div
                  part="icon-slot"
                  class=${rightIconClasses}
                  ?filled=${true}
                  @click=${(e: MouseEvent) => this.handleIconClick("right", e)}
                >
                  <slot
                    name="icon-right"
                    @slotchange=${this.onIconSlotChange}
                  ></slot>
                </div>`
              : html`<slot
                  name="icon-right"
                  style="display:none"
                  @slotchange=${this.onIconSlotChange}
                ></slot>`,
        )}
        <sky-icon
          class="dropdown-button"
          part="dropdown-button"
          ?compact=${this.compact}
          @click=${(e: MouseEvent) => {
            e.stopPropagation();
            if (!this.disabled && !this.readonly) {
              this.toggleDropdown(e);
            }
          }}
          icon=${this.open ? "ion:ios-arrow-up" : "ion:chevron-down"}
        >
        </sky-icon>
      </div>
    `;

    const defaultFieldContent = html`
      ${leftIcon}
      <div class="field-main" part="field-main">${valueArea}</div>
      ${this.readonly ? nothing : trailingActions}
    `;

    const insideFieldContent = html`
      ${leftIcon}
      <div class="input-inside-main">
        ${labelEl}
        <div class="input-inside-row">
          <div class="field-main" part="field-main">${valueArea}</div>
        </div>
      </div>
      ${this.readonly ? nothing : trailingActions}
    `;

    const fieldBody = isInside ? insideFieldContent : defaultFieldContent;

    const wrapperHandlers = {
      click: (e: MouseEvent) => {
        if (!this.disabled && !this.readonly) {
          this.toggleDropdown(e);
        }
      },
      keydown: this.handleKeydown,
    };

    const fieldWrapper = isFieldset
      ? html`
          <fieldset
            class="input-wrapper"
            tabindex="0"
            part="input-wrapper"
            ?disabled=${this.disabled}
            ?compact=${this.compact}
            ?loading=${this.loading}
            ?open=${this.open}
            ?invalid=${this.invalid}
            @click=${wrapperHandlers.click}
            @keydown=${wrapperHandlers.keydown}
          >
            ${labelEl}
            <div class="input-fieldset-body">${fieldBody}</div>
          </fieldset>
        `
      : html`
          <div
            class="input-wrapper"
            tabindex="0"
            part="input-wrapper"
            ?disabled=${this.disabled}
            ?compact=${this.compact}
            ?loading=${this.loading}
            ?open=${this.open}
            ?invalid=${this.invalid}
            @click=${wrapperHandlers.click}
            @keydown=${wrapperHandlers.keydown}
          >
            ${fieldBody}
          </div>
        `;

    return html`
      <div class="combo-box-container" part="combo-box-container">
        ${!isFieldset && !isInside ? labelEl : nothing}
        ${fieldWrapper}
        ${this.renderHiddenChipsDropdown()} ${this.renderDropdown()}
        ${this.renderValidationErrors()}
        <slot style="display:none" @slotchange=${this.refreshOptionSlots}></slot>
      </div>
    `;
  }
}
